import React, { Fragment, useState } from 'react';

import { JBColors } from '@bjb-gcrm/gcrm-frontend-components';
import { styled, TableCell, TableRow, Typography } from '@mui/material';
import moment from 'moment';
import { ReactComponent as ArrowIcon } from 'assets/icons/ArrowDown.svg';
import { isNotNullOrUndefined } from 'utils/utils';
import { arrayUniqueByKey, formatEnum } from 'utils/stringUtil';
import palette from 'theme/palette';
import { EditHistoryItem, EditHistoryItemDetail } from 'views/EditHistory/EditHistoryHeadcells';

const RevisionTitleTableCell = styled(TableCell)({
  border: 'none',
  verticalAlign: 'top',
  '&:last-of-type': {
    verticalAlign: 'bottom',
  },
});

const styles = {
  closedRowBorder: {
    borderBottom: `1px solid ${JBColors.app.lightGrey}`,
    '&:hover': {
      backgroundColor: JBColors.core.stone[10],
    },
  },
  expandedRow: {
    backgroundColor: palette.grey.lighter,
    '&:hover': {
      backgroundColor: JBColors.core.stone[20],
    },
    border: 'none',
    borderCollapse: 'collapse',
  },
  noBorder: {
    border: 'none',
    '&:hover': {
      backgroundColor: JBColors.core.stone[10],
    },
  },
  sectionTitleFont: {
    fontSize: '12px',
    fontWeight: 'bolder',
  },
  selectedRow: {
    backgroundColor: palette.grey.lighter,
  },
};

const closedCells = (
  row: EditHistoryItem,
  index: number,
  detailsWithTab: EditHistoryItemDetail[],
  isOpen: boolean,
  tabsInRevision: string[],
) => {
  return [
    {
      width: '12%',
      content: index === 0 && row.editedAt ? moment(row.editedAt).format('DD.MM.YYYY hh:mm') : '',
    },
    { width: '12%', content: index === 0 && row?.editedByFullName },
    { width: '12%', content: formatEnum(detailsWithTab[0].profileTab) },
    {
      width: '12%',
      content: Array.from(new Set(detailsWithTab.map((detail: EditHistoryItemDetail) => detail.section))).map(
        (detail, index) => (
          <Typography
            key={`${detail}-${index}`}
            sx={{
              fontSize: '12px',
              fontWeight: 'light',
            }}
          >
            {detail}
          </Typography>
        ),
      ),
    },

    { width: '22%' },
    { width: '22%' },
    {
      width: '8%',
      sx: { textAlign: 'right' },
      content: index === tabsInRevision.length - 1 && (
        <>
          Details <ArrowIcon style={{ rotate: isOpen ? '180deg' : undefined }} />
        </>
      ),
    },
  ];
};

const openTitleCells = (detailsWithSection: EditHistoryItemDetail[], section: string) => [
  { width: '12%', sx: { borderBottom: `1px solid ${palette.grey.lighter}` } },
  { width: '12%', sx: { borderBottom: `1px solid ${palette.grey.lighter}` } },
  {
    width: '12%',
    sx: { border: `none` },
    content: <Typography sx={styles.sectionTitleFont}>{formatEnum(detailsWithSection[0].profileTab)}</Typography>,
  },
  {
    width: '12%',
    sx: { border: 'none' },
    content: <Typography sx={styles.sectionTitleFont}>{section}</Typography>,
  },
  { width: '22%', sx: { border: 'none' } },
  { width: '22%', sx: { border: 'none' } },
  { width: '8%', sx: { border: 'none' } },
];

const openDetailCells = (detailsWithSection: EditHistoryItemDetail[], detail: EditHistoryItemDetail) => [
  { width: '12%', sx: { borderBottom: `1px solid ${palette.grey.lighter}` } },
  { width: '12%', sx: { borderBottom: `1px solid ${palette.grey.lighter}` } },
  { width: '12%', sx: { border: `none` } },
  { width: '12%', sx: { border: `none` }, content: detail.fieldName },
  {
    width: '22%',
    sx: { border: 'none' },
    content: formatEnum(detail.oldValue || ''),
  },
  {
    width: '22%',
    sx: { border: 'none' },
    content: formatEnum(detail.newValue || ''),
  },
  { width: '8%', sx: { border: 'none' } },
];

/**
 * Table of the detailed view within the revision history table.
 */
const EditHistoryItemDetailedView = ({ row }: { row: EditHistoryItem | null }) => {
  const [expanded, setExpanded] = useState(false);

  const tabsInRevision = isNotNullOrUndefined(row) ? arrayUniqueByKey(row.details, 'profileTab') : [];
  const sectionsInRevision = isNotNullOrUndefined(row) ? arrayUniqueByKey(row.details, 'section') : [];

  if (!row) {
    return null;
  }

  return expanded ? (
    <>
      {sectionsInRevision.map((section: string, index: number) => {
        const detailsWithSection = row?.details.filter((detail) => detail.section === section);
        return (
          <Fragment key={`${section}-${index}-detail`}>
            <TableRow
              sx={{
                ...styles.expandedRow,
                borderTop: `1px dashed ${JBColors.app.lightGrey}`,
              }}
              onClick={() => setExpanded(false)}
            >
              {openTitleCells(detailsWithSection, section).map((cell, cellIndex) => (
                <TableCell sx={{ ...cell.sx, width: cell.width }} key={`detailtitlecell-${cellIndex}`}>
                  {cell.content}
                </TableCell>
              ))}
            </TableRow>
            {detailsWithSection.map((detail: EditHistoryItemDetail, rowIndex) => (
              <TableRow
                sx={styles.expandedRow}
                onClick={() => setExpanded(false)}
                key={`${detail.fieldName}-${rowIndex}`}
              >
                {openDetailCells(detailsWithSection, detail).map((cell, cellIndex) => (
                  <TableCell sx={{ ...cell.sx, width: cell.width }} key={`detail-${cellIndex}`}>
                    {cell.content}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </Fragment>
        );
      })}
    </>
  ) : (
    <>
      {tabsInRevision.map((profileTab: string, index: number) => {
        const detailsWithTab = row?.details.filter((detail) => detail.profileTab === profileTab);
        return (
          <TableRow
            sx={index === tabsInRevision.length - 1 ? styles.closedRowBorder : styles.noBorder}
            key={`${profileTab}-${index}-row`}
            onClick={() => setExpanded(!expanded)}
          >
            {closedCells(row, index, detailsWithTab, expanded, tabsInRevision).map((cell, index) => (
              <RevisionTitleTableCell width={cell.width} sx={cell.sx} key={`titlecell-${index}`}>
                {cell.content}
              </RevisionTitleTableCell>
            ))}
          </TableRow>
        );
      })}
    </>
  );
};

export default EditHistoryItemDetailedView;
